import type { Dispatch, SetStateAction } from 'react';
import { useCallback } from 'react';
import { toTrimmedNonEmptyString } from '../../../../lib/utils/type-guards';
import type { StreamingLogger } from '../../chat-helpers';
import type { Message } from '../../chat-types';
import { completeAssistantMessage, setAssistantErrorMessage } from './helpers';
import type { SubmissionErrorClassification } from './types';

type UseStreamingErrorsOptions = {
  log: StreamingLogger;
  setMessages: Dispatch<SetStateAction<Message[]>>;
};

export type UseStreamingErrorsReturn = {
  applyAbortMessage: (assistantId: string) => void;
  applyFriendlyError: (assistantId: string, friendlyMessage: string) => void;
  classifySubmissionError: (error: unknown) => SubmissionErrorClassification;
  handleStreamError: (errorMessage: string) => void;
};

export const useStreamingErrors = ({ log, setMessages }: UseStreamingErrorsOptions): UseStreamingErrorsReturn => {
  const handleStreamError = useCallback(
    (errorMessage: string) => {
      console.error('Stream error:', errorMessage);
      log.error('Stream error', { error: errorMessage });
    },
    [log]
  );

  const applyAbortMessage = useCallback(
    (assistantId: string) => {
      setMessages((previous) => {
        const completed = completeAssistantMessage(previous, assistantId);
        return completed.map((message) => {
          if (message.id !== assistantId) {
            return message;
          }
          if (toTrimmedNonEmptyString(message.content)) {
            return message;
          }
          const { toolInvocations: _omit, ...rest } = message;
          return {
            ...rest,
            content: '',
            visibility: 'hidden',
          };
        });
      });
    },
    [setMessages]
  );

  const applyFriendlyError = useCallback(
    (assistantId: string, friendlyMessage: string) => {
      setMessages((previous) => setAssistantErrorMessage(previous, assistantId, friendlyMessage));
    },
    [setMessages]
  );

  const classifySubmissionError = useCallback(
    (error: unknown): SubmissionErrorClassification => {
      if (error instanceof Error && error.name === 'AbortError') {
        return { type: 'abort' };
      }

      if (error instanceof Error) {
        console.error('Chat error:', error);
        log.error('Chat submit error', { message: error.message, name: error.name });
        const friendly =
          error.message && error.message !== 'Failed to send message'
            ? `Chat error: ${error.message}`
            : 'Sorry, I encountered an error. Please try again.';
        return { type: 'error', friendlyMessage: friendly };
      }

      log.error('Chat submit error', { message: 'Unknown error', name: 'UnknownError' });
      return { type: 'error', friendlyMessage: 'Sorry, I encountered an error. Please try again.' };
    },
    [log]
  );

  return {
    applyAbortMessage,
    applyFriendlyError,
    classifySubmissionError,
    handleStreamError,
  };
};
